import { Fragment, useState, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Badge,
  Button,
  Drawer,
  styled,
  IconButton,
  ThemeProvider,
  Typography,
  Chip,
  Stack,
  Avatar,
  Tooltip,
  Divider,
  CircularProgress,
  alpha,
  useTheme,
  Collapse,
} from "@mui/material";
import {
  Clear,
  Notifications,
  NotificationsActive,
  Close,
  Refresh,
  Phone,
  WhatsApp,
  CakeOutlined,
  FavoriteOutlined,
  AccessTimeOutlined,
  CalendarMonth,
  ArrowForward,
  OpenInNewOutlined,
  CheckCircleOutline,
  KeyboardArrowDown,
  KeyboardArrowUp,
  LocationOnOutlined,
} from "@mui/icons-material";
import moment from "moment";

import useSettings from "app/hooks/useSettings";
import useNotification from "app/hooks/useNotification";
import useAuth from "app/hooks/useAuth";
import useAxiosPrivate from "app/hooks/useAxiosPrivate";
import { capitalizeWords } from "functions";
import { getTimeDifference } from "app/utils/utils.js";
import { topBarHeight } from "app/utils/constant";
import { themeShadows } from "../MatxTheme/themeColors";
import { Paragraph, Small } from "../Typography";

import FollowupDetailsDialog from "app/views/dashboard/shared/FollowupDetailsDialog";
import CelebrationDetailsDialog from "app/views/dashboard/shared/CelebrationDetailsDialog";

// ─── STYLED COMPONENTS ───────────────────────────────────────────────────────

const HeaderBox = styled("div")(({ theme }) => ({
  padding: "16px 20px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  height: topBarHeight + 8,
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
  background:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.background.paper, 0.95)
      : alpha("#ffffff", 0.95),
  backdropFilter: "blur(8px)",
  position: "sticky",
  top: 0,
  zIndex: 10,
}));

const NotificationCardWrapper = styled(Card)(({ theme }) => ({
  position: "relative",
  borderRadius: 12,
  padding: "12px 14px",
  marginBottom: 10,
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  boxShadow: theme.palette.mode === "dark"
    ? "0 2px 8px rgba(0,0,0,0.25)"
    : "0 2px 10px rgba(0,0,0,0.04)",
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    transform: "translateY(-1.5px)",
    boxShadow: theme.palette.mode === "dark"
      ? "0 6px 16px rgba(0,0,0,0.35)"
      : "0 6px 18px rgba(0,0,0,0.08)",
  },
}));

export default function NotificationBar({ container }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const { user } = useAuth();
  const axiosPrivate = useAxiosPrivate();

  const [panelOpen, setPanelOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState("all"); // 'all' | 'followup' | 'birthday' | 'anniversary'
  const [showSystemAlerts, setShowSystemAlerts] = useState(false);

  // Live items fetched from backend
  const [todayFollowups, setTodayFollowups] = useState([]);
  const [todayBirthdays, setTodayBirthdays] = useState([]);
  const [todayAnniversaries, setTodayAnniversaries] = useState([]);

  // Modals
  const [selectedFollowup, setSelectedFollowup] = useState(null);
  const [selectedCelebration, setSelectedCelebration] = useState(null);

  // Existing mock notification context
  const { deleteNotification, clearNotifications, notifications } =
    useNotification();

  const handleDrawerToggle = () => setPanelOpen((prev) => !prev);

  // ── Fetch today's real data from backend ─────────────────────────────────
  const fetchTodayData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const today = moment().format("YYYY-MM-DD");
      const todayDDMM = moment().format("DD/MM");
      const todayMMDD = moment().format("MM-DD");

      const [fRes, bRes, aRes] = await Promise.allSettled([
        axiosPrivate.get("Backend/LoadAllPendingFollowUps.php"),
        axiosPrivate.get("Backend/LoadReferenceBirthday.php"),
        axiosPrivate.get("Backend/LoadReferenceAnniversary.php"),
      ]);

      // Followups
      if (fRes.status === "fulfilled" && fRes.value?.data) {
        const raw = Array.isArray(fRes.value.data)
          ? fRes.value.data
          : fRes.value.data?.PendingFollowUps || [];
        const tf = raw.filter((f) => {
          const cat = f.DateCategory?.toLowerCase();
          if (cat === "today") return true;
          if (f.NextFollowUpDate && typeof f.NextFollowUpDate === "string") {
            return f.NextFollowUpDate.startsWith(today);
          }
          return false;
        });
        setTodayFollowups(tf);
      }

      // Birthdays
      if (bRes.status === "fulfilled" && Array.isArray(bRes.value?.data)) {
        const tb = bRes.value.data.filter((b) => {
          if (b.DateCategory?.toLowerCase() === "today") return true;
          if (b.CelebrationDate && typeof b.CelebrationDate === "string") {
            return (
              b.CelebrationDate.slice(5) === todayMMDD ||
              b.CelebrationDate.slice(0, 5) === todayDDMM
            );
          }
          return false;
        });
        setTodayBirthdays(tb);
      }

      // Anniversaries
      if (aRes.status === "fulfilled" && Array.isArray(aRes.value?.data)) {
        const ta = aRes.value.data.filter((a) => {
          if (a.DateCategory?.toLowerCase() === "today") return true;
          if (a.CelebrationDate && typeof a.CelebrationDate === "string") {
            return (
              a.CelebrationDate.slice(5) === todayMMDD ||
              a.CelebrationDate.slice(0, 5) === todayDDMM
            );
          }
          return false;
        });
        setTodayAnniversaries(ta);
      }
    } catch (err) {
      console.error("Error fetching notification bar data:", err);
    } finally {
      setLoading(false);
    }
  }, [axiosPrivate, user]);

  // Load once on mount or when user changes
  useEffect(() => {
    fetchTodayData();
  }, [fetchTodayData]);

  // Total counts
  const totalAgendaCount =
    todayFollowups.length + todayBirthdays.length + todayAnniversaries.length;

  // Filtered list to display in drawer
  const displayedItems = useMemo(() => {
    const list = [];

    // Follow-ups
    if (filterType === "all" || filterType === "followup") {
      todayFollowups.forEach((f) => {
        list.push({
          id: `f-${f.ID || f.UniqueID || Math.random()}`,
          category: "followup",
          title: capitalizeWords(f.Name || "Prospect"),
          subtitle: f.Discussed ? `Topic: ${f.Discussed}` : "Follow-up due today",
          time: f.NextFollowUpTime || f.NextFollowUpDate || "Today",
          location: [f.Village, f.Tehsil].filter(Boolean).join(", "),
          mobile: f.MobileNo || f.WhatsAppNo,
          raw: f,
        });
      });
    }

    // Birthdays
    if (filterType === "all" || filterType === "birthday") {
      todayBirthdays.forEach((b) => {
        const name = capitalizeWords(b.FamilyMember || b.Name || "Celebrant");
        list.push({
          id: `b-${b.ID || b.MainMemberID || Math.random()}`,
          category: "birthday",
          title: name,
          subtitle: b.YearsCompleted != null ? `Turning ${b.YearsCompleted} Years Old!` : "Birthday Today! 🎂",
          location: [b.Village, b.Tehsil, b.District].filter(Boolean).join(", "),
          mobile: b.Mobile || b.WhatsAppNo,
          raw: b,
        });
      });
    }

    // Anniversaries
    if (filterType === "all" || filterType === "anniversary") {
      todayAnniversaries.forEach((a) => {
        const name = capitalizeWords(a.FamilyMember || a.Name || "Couple");
        list.push({
          id: `a-${a.ID || a.MainMemberID || Math.random()}`,
          category: "anniversary",
          title: name,
          subtitle: a.YearsCompleted != null ? `${a.YearsCompleted}th Wedding Anniversary!` : "Wedding Anniversary! 💑",
          location: [a.Village, a.Tehsil, a.District].filter(Boolean).join(", "),
          mobile: a.Mobile || a.WhatsAppNo,
          raw: a,
        });
      });
    }

    return list;
  }, [filterType, todayFollowups, todayBirthdays, todayAnniversaries]);

  // Action handlers
  const handleCall = (mobile) => {
    if (!mobile) return;
    window.open(`tel:${mobile}`);
  };

  const handleWhatsAppWish = (mobile, name, type, years) => {
    if (!mobile) return;
    const firstName = capitalizeWords((name || "").split(" ")[0]);
    let msg = "";
    if (type === "birthday") {
      msg = `Dear ${firstName},\n\nWishing you a very Happy Birthday! 🎂🎉 May your year ahead be filled with abundant health, happiness, and prosperity!\n\nWarm regards,\n${user?.Name || "Dahiya Insurance Team"}`;
    } else {
      msg = `Dear ${firstName},\n\nHeartiest Congratulations on your ${years ? `${years}th ` : ""}Wedding Anniversary! 💑💐 Wishing you both a lifetime of happiness, peace, and togetherness!\n\nWarm regards,\n${user?.Name || "Dahiya Insurance Team"}`;
    }
    window.open(`https://wa.me/91${mobile}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const handleWhatsAppFollowup = (mobile, name, nextDate, topic) => {
    if (!mobile) return;
    const firstName = capitalizeWords((name || "").split(" ")[0]);
    const dateText = nextDate && !nextDate.startsWith("00/00/0000") ? `scheduled on ${nextDate}` : "scheduled for today";
    const msg = `Hello ${firstName},\n\nGentle reminder regarding our scheduled follow-up ${dateText}${topic ? ` to discuss "${topic}"` : ""}.\nLooking forward to speaking with you!\n\nWarm regards,\n${user?.Name || "Dahiya Insurance Team"}`;
    window.open(`https://wa.me/91${mobile}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <Fragment>
      {/* Top Bar Bell Button with Badge */}
      <Tooltip title={totalAgendaCount > 0 ? `${totalAgendaCount} Due Today (Followups & Celebrations)` : "Notifications"}>
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            transition: "transform 0.2s ease",
            "&:hover": { transform: "scale(1.08)" },
          }}
        >
          <Badge
            badgeContent={
              totalAgendaCount > 0
                ? totalAgendaCount
                : Array.isArray(notifications) && notifications.length > 0
                  ? notifications.length
                  : undefined
            }
            color={totalAgendaCount > 0 ? "error" : "secondary"}
            sx={{
              "& .MuiBadge-badge": {
                fontWeight: 700,
                fontSize: "0.72rem",
                height: 18,
                minWidth: 18,
                padding: "0 4px",
              },
            }}
          >
            {totalAgendaCount > 0 ? (
              <NotificationsActive
                sx={{
                  color: totalAgendaCount > 0 ? "error.main" : "text.primary",
                }}
              />
            ) : (
              <Notifications sx={{ color: "text.primary" }} />
            )}
          </Badge>
        </IconButton>
      </Tooltip>

      {/* Drawer */}
      <ThemeProvider theme={settings.themes[settings.activeTheme]}>
        <Drawer
          container={container}
          variant="temporary"
          anchor="right"
          open={panelOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              width: { xs: "100vw", sm: 390 },
              maxWidth: "100vw",
              bgcolor: "background.default",
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          {/* Header */}
          <HeaderBox>
            <Box display="flex" alignItems="center" gap={1.2}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Notifications sx={{ fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                  Today's Agenda & Alerts
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {moment().format("dddd, D MMMM YYYY")}
                </Typography>
              </Box>
            </Box>

            <Stack direction="row" spacing={0.5} alignItems="center">
              <Tooltip title="Refresh Notifications">
                <IconButton
                  size="small"
                  onClick={fetchTodayData}
                  disabled={loading}
                  sx={{ p: 0.6 }}
                >
                  <Refresh
                    sx={{
                      fontSize: 19,
                      animation: loading ? "spin 1s linear infinite" : "none",
                      "@keyframes spin": {
                        "0%": { transform: "rotate(0deg)" },
                        "100%": { transform: "rotate(360deg)" },
                      },
                    }}
                  />
                </IconButton>
              </Tooltip>
              <IconButton size="small" onClick={handleDrawerToggle} sx={{ p: 0.6 }}>
                <Close sx={{ fontSize: 19 }} />
              </IconButton>
            </Stack>
          </HeaderBox>

          {/* Filter Chips Bar */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              bgcolor: (t) =>
                t.palette.mode === "dark"
                  ? alpha(t.palette.background.paper, 0.5)
                  : alpha(t.palette.background.paper, 0.8),
            }}
          >
            <Stack direction="row" spacing={0.8} sx={{ overflowX: "auto", pb: 0.2 }}>
              <Chip
                clickable
                size="small"
                onClick={() => setFilterType("all")}
                label={`All (${totalAgendaCount})`}
                variant={filterType === "all" ? "filled" : "outlined"}
                color={filterType === "all" ? "primary" : "default"}
                sx={{ fontWeight: 700, fontSize: "0.72rem", height: 26, borderRadius: 2 }}
              />

              <Chip
                clickable
                size="small"
                onClick={() => setFilterType("followup")}
                label={`Followups (${todayFollowups.length})`}
                variant={filterType === "followup" ? "filled" : "outlined"}
                sx={{
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  height: 26,
                  borderRadius: 2,
                  borderColor: alpha(theme.palette.warning.main, 0.4),
                  color: theme.palette.warning.dark,
                  bgcolor: filterType === "followup" ? alpha(theme.palette.warning.main, 0.15) : "transparent",
                }}
              />

              <Chip
                clickable
                size="small"
                onClick={() => setFilterType("birthday")}
                label={`Birthdays (${todayBirthdays.length})`}
                variant={filterType === "birthday" ? "filled" : "outlined"}
                sx={{
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  height: 26,
                  borderRadius: 2,
                  borderColor: alpha("#F59E0B", 0.4),
                  color: "#D97706",
                  bgcolor: filterType === "birthday" ? alpha("#F59E0B", 0.15) : "transparent",
                }}
              />

              <Chip
                clickable
                size="small"
                onClick={() => setFilterType("anniversary")}
                label={`Anniv. (${todayAnniversaries.length})`}
                variant={filterType === "anniversary" ? "filled" : "outlined"}
                sx={{
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  height: 26,
                  borderRadius: 2,
                  borderColor: alpha("#EC4899", 0.4),
                  color: "#BE185D",
                  bgcolor: filterType === "anniversary" ? alpha("#EC4899", 0.15) : "transparent",
                }}
              />
            </Stack>
          </Box>

          {/* Body Content */}
          <Box sx={{ p: 2, flex: 1, overflowY: "auto" }}>
            {loading ? (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6}>
                <CircularProgress size={32} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Loading today's reminders...
                </Typography>
              </Box>
            ) : displayedItems.length === 0 ? (
              <Box
                sx={{
                  textAlign: "center",
                  py: 6,
                  px: 2,
                  borderRadius: 3,
                  bgcolor: (t) => alpha(t.palette.action.hover, 0.4),
                  border: (t) => `1px dashed ${alpha(t.palette.divider, 0.2)}`,
                }}
              >
                <CheckCircleOutline sx={{ fontSize: 44, color: "success.main", mb: 1, opacity: 0.85 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "text.primary" }}>
                  All Caught Up!
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                  No {filterType === "all" ? "follow-ups or celebrations" : filterType} scheduled for today.
                </Typography>
              </Box>
            ) : (
              displayedItems.map((item) => {
                const isFollowup = item.category === "followup";
                const isBirthday = item.category === "birthday";
                const isAnniv = item.category === "anniversary";

                const accentColor = isBirthday
                  ? "#F59E0B"
                  : isAnniv
                    ? "#EC4899"
                    : theme.palette.warning.main;

                return (
                  <NotificationCardWrapper key={item.id}>
                    {/* Top Row: Type Avatar, Name, Category Badge */}
                    <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
                      <Box display="flex" alignItems="center" gap={1.2}>
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: alpha(accentColor, 0.12),
                            color: accentColor,
                          }}
                        >
                          {isBirthday && <CakeOutlined sx={{ fontSize: 20 }} />}
                          {isAnniv && <FavoriteOutlined sx={{ fontSize: 20 }} />}
                          {isFollowup && <AccessTimeOutlined sx={{ fontSize: 20 }} />}
                        </Avatar>

                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary", lineHeight: 1.2 }}>
                            {item.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            {item.subtitle}
                          </Typography>
                        </Box>
                      </Box>

                      <Chip
                        label={
                          isBirthday
                            ? "Birthday"
                            : isAnniv
                              ? "Anniversary"
                              : "Follow-up"
                        }
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: "0.6rem",
                          fontWeight: 800,
                          bgcolor: alpha(accentColor, 0.12),
                          color: accentColor,
                          "& .MuiChip-label": { px: 0.7 },
                        }}
                      />
                    </Box>

                    {/* Secondary Details: Location & Time */}
                    {(item.location || (isFollowup && item.time)) && (
                      <Box display="flex" alignItems="center" gap={1.5} mt={1} pl={5.5}>
                        {item.location && (
                          <Box display="flex" alignItems="center" gap={0.3}>
                            <LocationOnOutlined sx={{ fontSize: 13, color: "text.secondary" }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                              {item.location}
                            </Typography>
                          </Box>
                        )}
                        {isFollowup && item.time && (
                          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem", fontWeight: 600 }}>
                            ⏰ {item.time}
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Action Buttons Row */}
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mt={1.2}
                      pt={1}
                      borderTop={`1px solid ${alpha(theme.palette.divider, 0.08)}`}
                    >
                      <Stack direction="row" spacing={0.6}>
                        {/* Call */}
                        {item.mobile && (
                          <Tooltip title={`Call ${item.mobile}`}>
                            <IconButton
                              size="small"
                              onClick={() => handleCall(item.mobile)}
                              sx={{
                                width: 28,
                                height: 28,
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                                color: "primary.main",
                                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.18) },
                              }}
                            >
                              <Phone sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* WhatsApp */}
                        {item.mobile && (
                          <Tooltip title={isFollowup ? "Send WhatsApp Reminder" : "Send WhatsApp Greeting"}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                if (isFollowup) {
                                  handleWhatsAppFollowup(
                                    item.mobile,
                                    item.title,
                                    item.raw?.NextFollowUpDate,
                                    item.raw?.Discussed
                                  );
                                } else {
                                  handleWhatsAppWish(
                                    item.mobile,
                                    item.title,
                                    item.category,
                                    item.raw?.YearsCompleted
                                  );
                                }
                              }}
                              sx={{
                                width: 28,
                                height: 28,
                                bgcolor: alpha("#25D366", 0.1),
                                color: "#25D366",
                                "&:hover": { bgcolor: alpha("#25D366", 0.22) },
                              }}
                            >
                              <WhatsApp sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>

                      {/* Details Dialog Trigger */}
                      <Button
                        size="small"
                        endIcon={<ArrowForward sx={{ fontSize: 12 }} />}
                        onClick={() => {
                          if (isFollowup) {
                            setSelectedFollowup(item.raw);
                          } else {
                            setSelectedCelebration({ data: item.raw, type: item.category });
                          }
                        }}
                        sx={{
                          textTransform: "none",
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          py: 0.2,
                          px: 1,
                          color: "text.primary",
                          borderRadius: 1.5,
                          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                        }}
                      >
                        Details
                      </Button>
                    </Box>
                  </NotificationCardWrapper>
                );
              })
            )}

            {/* Optional System Alerts Collapsible */}
            {Array.isArray(notifications) && notifications.length > 0 && (
              <Box mt={3} pt={2} borderTop={`1px dashed ${alpha(theme.palette.divider, 0.2)}`}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ cursor: "pointer", py: 0.5 }}
                  onClick={() => setShowSystemAlerts(!showSystemAlerts)}
                >
                  <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", letterSpacing: "0.5px" }}>
                    SYSTEM ALERTS ({notifications.length})
                  </Typography>
                  <IconButton size="small">
                    {showSystemAlerts ? <KeyboardArrowUp sx={{ fontSize: 16 }} /> : <KeyboardArrowDown sx={{ fontSize: 16 }} />}
                  </IconButton>
                </Box>

                <Collapse in={showSystemAlerts}>
                  <Box mt={1}>
                    {notifications.map((n) => (
                      <Card
                        key={n.id}
                        sx={{
                          p: 1.2,
                          mb: 1,
                          borderRadius: 2,
                          bgcolor: (t) => alpha(t.palette.background.paper, 0.6),
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>
                            {n.title}
                          </Typography>
                          <IconButton size="small" onClick={() => deleteNotification(n.id)} sx={{ p: 0.2 }}>
                            <Clear sx={{ fontSize: 12 }} />
                          </IconButton>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          {n.subtitle}
                        </Typography>
                      </Card>
                    ))}
                    <Button size="small" onClick={clearNotifications} sx={{ fontSize: "0.68rem", textTransform: "none", mt: 0.5 }}>
                      Clear System Alerts
                    </Button>
                  </Box>
                </Collapse>
              </Box>
            )}
          </Box>

          {/* Footer Shortcuts */}
          <Box
            sx={{
              p: 2,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
              bgcolor: (t) =>
                t.palette.mode === "dark"
                  ? alpha(t.palette.background.paper, 0.95)
                  : alpha("#ffffff", 0.95),
            }}
          >
            <Stack direction="row" spacing={1}>
              <Button
                component={Link}
                to="/Calendar"
                onClick={handleDrawerToggle}
                variant="outlined"
                fullWidth
                size="small"
                startIcon={<CalendarMonth sx={{ fontSize: 16 }} />}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  borderRadius: 2,
                }}
              >
                Calendar
              </Button>

              <Button
                component={Link}
                to="/Followups"
                onClick={handleDrawerToggle}
                variant="contained"
                fullWidth
                size="small"
                endIcon={<ArrowForward sx={{ fontSize: 15 }} />}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  borderRadius: 2,
                  boxShadow: "none",
                }}
              >
                All Follow-ups
              </Button>
            </Stack>
          </Box>
        </Drawer>
      </ThemeProvider>

      {/* ── Details Dialogs rendered globally from notification bar ── */}
      <FollowupDetailsDialog
        open={Boolean(selectedFollowup)}
        onClose={() => setSelectedFollowup(null)}
        followup={selectedFollowup}
      />

      <CelebrationDetailsDialog
        open={Boolean(selectedCelebration)}
        onClose={() => setSelectedCelebration(null)}
        data={selectedCelebration?.data}
        type={selectedCelebration?.type}
      />
    </Fragment>
  );
}
